import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getRetry } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { HeroCanvas } from "../components/HeroCanvas";
import { CourseCard } from "../components/CourseCard";
import { BundleCard } from "../components/BundleCard";

const FAQS = [
  { q: "Do I need coding experience?", a: "No prior coding is needed to start. Courses span multiple areas of Unreal Engine — Blueprints (visual scripting), level design, and more topics like character work coming over time. Where a little code or an extra tool genuinely helps, the course walks you through it. Each course page tells you exactly what it uses." },
  { q: "Which Unreal Engine version do the courses use?", a: "Courses are recorded in recent versions of Unreal Engine, and the exact version varies by course. The engine updates constantly, but the core concepts and workflows you learn carry across versions. Everything works with the free version of Unreal Engine." },
  { q: "Are project files included?", a: "Most courses include project files, assets, and downloadable resources. What exactly is included varies by course — check the \"This course includes\" list on each course page." },
  { q: "How do refunds work?", a: "Every paid course comes with a 14-day money-back guarantee. Request a refund from your Account page — if you've watched 20% or more of the course, the request is flagged for manual review." },
  { q: "How do I access my courses after buying?", a: "Instantly and forever. Go to My Learning to watch anytime — your progress is saved per lecture and you can resume exactly where you left off, on any device." },
];

function useReveal(dep) {
  useEffect(() => {
    const obs = new IntersectionObserver((es) => es.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add("in"); obs.unobserve(en.target); }
    }), { threshold: 0.1 });
    document.querySelectorAll(".reveal:not(.in)").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [dep]);
}

export default function Home() {
  const { sections, refreshSections } = useStore();
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [status, setStatus] = useState("loading");
  const [openFaq, setOpenFaq] = useState(null);
  const nav = useNavigate();
  const loadCourses = () => {
    setStatus("loading");
    getRetry("/courses")
      .then((r) => { setCourses(r.data); setStatus("ok"); })
      .catch((e) => { console.warn("Courses load failed:", e?.message); setStatus("error"); });
  };
  useEffect(() => {
    loadCourses();
    getRetry("/bundles").then((r) => setBundles(r.data)).catch((e) => console.warn("Bundles load failed:", e?.message));
  }, []);
  useReveal(courses.length + sections.length + bundles.length);

  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const visibleSections = sections.filter((s) => s.enabled !== false && s.counts?.total > 0);
  const totalLects = courses.reduce((n, c) => n + (c.lecture_count || 0), 0);

  return (
    <div>
      <header className="hero">
        <HeroCanvas />
        <div className="hero-fade" />
        <div className="hero-inner">
          <div className="eyebrow">◆ Unreal Engine Academy ◆</div>
          <h1>MASTER THE ENGINE<br /><span className="grad">BUILD YOUR WORLDS</span></h1>
          <p className="hero-sub">From your first Blueprint node to cinematic open-world landscapes — step-by-step courses built for creators who want results, not theory.</p>
          <div className="hero-cta">
            <button className="btn-primary btn-big" data-testid="hero-browse-btn" onClick={() => nav("/courses")}>Browse All Courses</button>
            <button className="btn-outline btn-big" onClick={() => document.querySelector('[data-testid="nav-search-input"]')?.focus()}>Search Courses</button>
          </div>
          <div className="hero-stats">
            <div className="stat"><b>{courses.length}</b><span>Courses</span></div>
            <div className="stat"><b>{totalLects}</b><span>Lectures</span></div>
            <div className="stat"><b>100%</b><span>Project-Based</span></div>
            <div className="stat"><b>∞</b><span>Lifetime Access</span></div>
          </div>
        </div>
      </header>

      <div className="wrap wrap-wide">
        {status === "error" && (
          <div className="empty" data-testid="catalog-load-error" style={{ marginTop: 32 }}>
            <p>Couldn't load the course catalog — please check your connection.</p>
            <button className="btn-primary mt-4" data-testid="catalog-retry-btn"
              onClick={() => { refreshSections(); loadCourses(); }}>RETRY</button>
          </div>
        )}
        {status === "loading" && (sections.length === 0 || courses.length === 0) && (
          <div className="sec-grid" style={{ marginTop: 48 }} data-testid="catalog-skeleton">
            {[1, 2, 3].map((i) => <div key={i} className="skel-card" />)}
          </div>
        )}
        <div className="pills">
          {visibleSections.map((s) => (
            <button key={s.id} className="pill" data-testid={`pill-${s.id}`} onClick={() => document.getElementById("sec-" + s.id)?.scrollIntoView({ behavior: "smooth" })}>{s.icon} {s.title}</button>
          ))}
          <button className="pill" data-testid="pill-all" onClick={() => nav("/courses")}>All Courses</button>
        </div>

        {visibleSections.map((s) => {
          const mine = courses.filter((c) => c.section === s.id);
          const dim = s.color === "land" ? "var(--land-dim)" : "var(--bp-dim)";
          const bor = s.color === "land" ? "rgba(82,210,115,.3)" : "rgba(56,182,255,.3)";
          return (
            <section className="section reveal" id={"sec-" + s.id} key={s.id}>
              <div className="sec-head">
                <div className="sec-title">
                  <div className="sec-icon" style={{ background: dim, border: `1px solid ${bor}` }}>{s.icon}</div>
                  <div><h2>{s.title.toUpperCase()}</h2><p>{s.tagline}</p></div>
                </div>
                <span className="sec-count">{mine.length} COURSE{mine.length !== 1 ? "S" : ""}</span>
              </div>
              <div className="sec-grid">{mine.map((c) => <CourseCard key={c.id} c={c} secMap={secMap} />)}</div>
              {mine.length === 0 && <div className="empty">Courses for this section will be announced soon.</div>}
            </section>
          );
        })}
        {bundles.length > 0 && (
          <section className="section reveal" id="sec-bundles" data-testid="bundles-section">
            <div className="sec-head">
              <div className="sec-title">
                <div className="sec-icon" style={{ background: "rgba(245,192,78,.12)", border: "1px solid rgba(245,192,78,.35)" }}>🎁</div>
                <div><h2>BUNDLE DEALS</h2><p>Multiple courses, one price — save big</p></div>
              </div>
              <span className="sec-count">{bundles.length} BUNDLE{bundles.length !== 1 ? "S" : ""}</span>
            </div>
            <div className="sec-grid">{bundles.map((b) => <BundleCard key={b.id} b={b} />)}</div>
          </section>
        )}
      </div>

      <div className="landscape-band reveal">
        <div className="sky" />
        <svg className="mtn" style={{ opacity: 0.35 }} viewBox="0 0 1200 300" preserveAspectRatio="none"><path d="M0,300 L0,180 L140,100 L260,170 L400,70 L540,160 L660,110 L800,190 L940,80 L1080,160 L1200,120 L1200,300 Z" fill="#132b33" /></svg>
        <svg className="mtn" style={{ opacity: 0.6 }} viewBox="0 0 1200 300" preserveAspectRatio="none"><path d="M0,300 L0,225 L120,160 L240,215 L380,135 L520,225 L640,170 L780,240 L920,150 L1060,225 L1200,180 L1200,300 Z" fill="#0f2f2a" /></svg>
        <svg className="mtn" viewBox="0 0 1200 300" preserveAspectRatio="none"><path d="M0,300 L0,265 L150,215 L300,265 L450,205 L600,275 L750,230 L900,285 L1050,225 L1200,265 L1200,300 Z" fill="#0a1f1a" /></svg>
        <div className="mist" />
        <div className="band-text"><div><h2>MASTER EVERY SIDE OF UNREAL ENGINE</h2><p>Project-based learning across Blueprints, level design and beyond — build real skills and portfolio work as you go.</p></div></div>
      </div>

      <div className="wrap">
        <section className="section reveal" id="faqRoot">
          <div className="sec-head"><div className="sec-title">
            <div className="sec-icon" style={{ background: "var(--bp-dim)", border: "1px solid rgba(56,182,255,.3)" }}>?</div>
            <div><h2>QUESTIONS, ANSWERED</h2><p>Everything you need to know before starting</p></div></div></div>
          <div className="faq">
            {FAQS.map((f, i) => (
              <div className={`faq-item ${openFaq === i ? "open" : ""}`} key={f.q}>
                <button className="faq-q" data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span><span className="pm">+</span>
                </button>
                <div className="faq-a"><div>{f.a}</div></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
